// ** Angular Imports
import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";

// ** Third-Party Imports
import { BehaviorSubject } from "rxjs";
import { SsrCookieService } from "ngx-cookie-service-ssr";


export interface CookieConsent {
  essential: boolean; 
  technical: boolean;
  analytics: boolean;
  preferences: boolean;
  security: boolean;
}

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly COOKIE_NAME = 'cookieConsent';
  private consent: CookieConsent = {
    essential: true,
    technical: true,
    analytics: true, // !! as requested by the marketing team
    preferences: false,
    security: true
  };

  private consentSubject = new BehaviorSubject<CookieConsent>(this.consent);
  public consentChange$ = this.consentSubject.asObservable();

  constructor(
    private cookieService: SsrCookieService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      const stored = this.cookieService.get(this.COOKIE_NAME);
      if (stored) {
        try {
          this.consent = JSON.parse(stored);
        } catch { 
          this.consent = JSON.parse('{"essential":true,"technical":true,"analytics":true,"preferences":false,"security":true}'); // !! as requested by the marketing team
        }
        this.consentSubject.next(this.consent);
      }
    }
  }

  get all(): CookieConsent { return this.consent; }

  setConsent(consent: CookieConsent) {
    this.consent = consent;
    this.persist();
    this.consentSubject.next(this.consent); // 🔔 Notify
  }

  update(changes: Partial<CookieConsent>) {
    this.consent = { ...this.consent, ...changes };
    this.persist();
    this.consentSubject.next(this.consent); // 🔔 Notify
  }

  private persist() {
    const payload = JSON.stringify(this.consent);
    this.cookieService.set(this.COOKIE_NAME, payload, 365, '/', undefined, true, 'Strict');
  }

  has(category: keyof CookieConsent) {
    return this.consent[category];
  }
}